import { type ParsedUrlQueryInput } from "querystring"
import { useRouter } from "next/router"
import { type NextWizard } from "@krakentech/blueprint-onboarding/next-wizard/pages-router"
import { type DefaultWizardState } from "./types"

export function createWizardToStep<
    TRouteNames extends Record<string, string>,
    TState extends DefaultWizardState<TRouteNames>,
    TSerializedState extends DefaultWizardState<TRouteNames>,
>(
    wizard: Pick<
        NextWizard<TRouteNames, TState, TSerializedState>,
        "getStep" | "getNextStep" | "setCookieStateOnClient" | "addToSubmittedSteps" | "toNextStep"
    >
) {
    function useWizardToStep(currentStepName: TRouteNames[keyof TRouteNames]) {
        const router = useRouter()

        const toStepWithoutCookieUpdate = async (
            stepName: TRouteNames[keyof TRouteNames],
            state: TState,
            queryParams?: ParsedUrlQueryInput
        ) => {
            const targetStep = wizard.getStep({ stepName, state })

            await router.push({
                pathname: targetStep.pathname,
                query: { ...targetStep.query, ...queryParams },
            })
        }

        const toStep = async (
            stepName: TRouteNames[keyof TRouteNames],
            state: TState,
            queryParams?: ParsedUrlQueryInput
        ) => {
            await wizard.setCookieStateOnClient({
                state: {
                    ...state,
                    submittedSteps: wizard.addToSubmittedSteps({
                        stepName: currentStepName,
                        submittedSteps: state.submittedSteps,
                    }),
                    stepHistory: wizard.addToSubmittedSteps({
                        stepName,
                        submittedSteps: state.stepHistory,
                    }),
                },
            })

            const targetStep = wizard.getStep({ stepName, state })

            await router.push({
                pathname: targetStep.pathname,
                query: { ...targetStep.query, ...queryParams },
            })
        }

        const toPreviousStep = async (state: TState) => {
            let previousStepIndex = state.submittedSteps.findIndex(
                (step) => step === currentStepName
            )

            if (previousStepIndex === -1) {
                previousStepIndex = state.submittedSteps.length
            }

            const previousStepName = state.submittedSteps[previousStepIndex - 1]
            if (!previousStepName) throw new Error("Previous step not found")

            const previousStep = wizard.getStep({
                state,
                stepName: previousStepName,
            })

            await router.push({
                pathname: previousStep.pathname,
                query: previousStep.query,
            })
        }

        const toNextStep = async (state: TState) => {
            const nextStep = wizard.getNextStep({
                currentStepName,
                state,
            })

            await wizard.setCookieStateOnClient({
                state: {
                    ...state,
                    submittedSteps: wizard.addToSubmittedSteps({
                        stepName: currentStepName,
                        submittedSteps: state.submittedSteps,
                    }),
                    stepHistory: wizard.addToSubmittedSteps({
                        stepName: nextStep.name,
                        submittedSteps: state.stepHistory,
                    }),
                },
            })

            await wizard.toNextStep({
                currentStepName,
                state,
                router,
            })
        }

        return { toStep, toStepWithoutCookieUpdate, toPreviousStep, toNextStep }
    }

    return useWizardToStep
}
